import React, { useEffect, useMemo, useState } from 'react';
import SettingContainer from '../SettingContainer';
import { useConfig } from '../../../DataStore/ConfigContext';
import ActivityLoader from '../../Common/ActivityLoader';
import Notification from '../../Common/Notification';
import { useTheme } from '../../../DataStore/ThemeContext';
import { usePermission } from '../../../DataStore/PermissionContext';

/* ---------- Small helpers ---------- */
const roleIcon = (name = '') => {
  const n = String(name).toLowerCase();
  if (n.includes('super')) return 'fa-shield-halved';
  if (n.includes('admin')) return 'fa-user-shield';
  return 'fa-user';
};
const tintFromId = (id) => {
  const colors = ['#E3F2FD', '#E8F5E9', '#FFF3E0', '#F3E5F5', '#E0F7FA', '#FCE4EC', '#EDE7F6', '#E0F2F1'];
  const idx = Math.abs(parseInt(id, 10) || 0) % colors.length;
  return colors[idx];
};

/* ---------- Aesthetic card for a role ---------- */
const RoleCard = ({ role, active, onSelect, onEdit, onPermissions }) => {
  const bg = tintFromId(role.id);
  return (
    <div
      className={`card shadow-sm ${active ? 'border-primary' : 'border-0'}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(role)}
      onKeyDown={(e) => (e.key === 'Enter' ? onSelect(role) : null)}
      style={{ transition: 'box-shadow .2s, transform .2s' }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'none')}
    >
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-start">
          <div
            className="rounded d-flex align-items-center justify-content-center mr-3"
            style={{
              width: 48,
              height: 48,
              background: bg,
              color: '#0F172A',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.04)',
            }}
          >
            <i className={`fa ${roleIcon(role.role_name)} fa-lg`} />
          </div>
          <div className="flex-grow-1">
            <div className="font-weight-semibold">{role.role_name}</div>
            <div className="text-muted small">Role ID: {role.id}</div>
          </div>
          {active && <i className="fa fa-check-circle text-primary ml-2" aria-label="selected" />}
        </div>

        {/* Actions */}
        <div className="mt-auto pt-3 d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={(e) => {
              e.stopPropagation();
              onPermissions(role);
            }}
          >
            <i className="fa fa-key mr-1" />
            Permissions
          </button>
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(role);
            }}
          >
            <i className="fa fa-pen mr-1" />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};

const UserRoles = () => {
  const { openSettings } = useTheme();
  const [activeRecord, setActiveRecord] = useState(null);
  const [query, setQuery] = useState('');
  const { getUserRoles, userRoles, errorUserRoles, loadingUserRoles, dispatch } = useConfig();
  const {setConfigRole } = usePermission();

  useEffect(() => {
    getUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNew = () => openSettings('New User Role', 'ConfigNewUserRole');
  const handleEdit = (role = activeRecord) => role && openSettings('Edit User Role', 'ConfigEditUserRole', role);
//   const handlePermissions = (role = activeRecord) =>
//     role && openSettings(`${role.role_name} • Permissions`, 'ConfigRolePermissions', role);

   const handlePermissions = (role)=>{
        setConfigRole(role);
        dispatch({type: 'activeMenu', payload: 15});
    }


  const filtered = useMemo(() => {
    const list = Array.isArray(userRoles) ? userRoles : [];
    const q = query.trim().toLowerCase();
    const base = q ? list.filter((r) => r.role_name.toLowerCase().includes(q)) : list;
    // Aesthetic ordering: Super-Admin, Admin, then by name
    return [...base].sort((a, b) => {
      const rank = (n) => (n.toLowerCase().includes('super') ? 0 : n.toLowerCase().includes('admin') ? 1 : 2);
      const ra = rank(a.role_name);
      const rb = rank(b.role_name);
      return ra !== rb ? ra - rb : a.role_name.localeCompare(b.role_name);
    });
  }, [userRoles, query]);

  const menu = () => (
    <>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={handleNew}>
        <i className="dropdown-icon fa fa-plus" /> <span>New Role</span>
      </button>
      <button type="button" tabIndex="0" className="dropdown-item" disabled={!activeRecord} onClick={() => handleEdit()}>
        <i className="dropdown-icon fa fa-edit" /> <span>Edit Selection</span>
      </button>
      <button type="button" tabIndex="0" className="dropdown-item" disabled={!activeRecord} onClick={() => handlePermissions()}>
        <i className="dropdown-icon fa fa-key" /> <span>Manage Permissions</span>
      </button>
    </>
  );

  return (
    <SettingContainer title="User Roles" icon="fa fa-user-shield" Menu={menu}>
      {/* Top toolbar */}
      <div className="d-flex align-items-center m-4 mb-3">
        <div className="input-group" style={{ maxWidth: 360 }}>
          <div className="input-group-prepend">
            <span className="input-group-text bg-white"><i className="fa fa-search" /></span>
          </div>
          <input
            type="text"
            className="form-control"
            placeholder="Search roles…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="ml-auto">
          <button className="btn btn-primary btn-sm" onClick={handleNew}>
            <i className="fa fa-plus mr-1" />
            New Role
          </button>
        </div>
      </div>

      {/* Body states */}
      {errorUserRoles && <Notification type="danger" message={String(errorUserRoles)} />}
      {loadingUserRoles && (
        <div className="py-4 text-center">
          <ActivityLoader />
        </div>
      )}

      {!loadingUserRoles && Array.isArray(userRoles) && userRoles.length === 0 && (
        <div className="text-center p-5 border rounded">
          <div className="mb-2">
            <span className="rounded-circle d-inline-flex align-items-center justify-content-center bg-light" style={{ width: 64, height: 64 }}>
              <i className="fa fa-users-cog text-muted" />
            </span>
          </div>
          <div className="h6">No roles yet</div>
          <div className="text-muted mb-3">Create roles to manage permissions across your team.</div>
          <button className="btn btn-primary btn-sm" onClick={handleNew}>
            <i className="fa fa-plus mr-1" /> New Role
          </button>
        </div>
      )}

      {!loadingUserRoles && filtered.length > 0 && (
        <div className="row">
          {filtered.map((role) => (
            <div className="col-lg-4 col-md-6 mb-3" key={role.id}>
              <RoleCard
                role={role}
                active={activeRecord?.id === role.id}
                onSelect={setActiveRecord}
                onEdit={handleEdit}
                onPermissions={handlePermissions}
              />
            </div>
          ))}
        </div>
      )}
    </SettingContainer>
  );
};

export default UserRoles;
